import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { ChevronRight } from 'lucide-react'
import { Collapsible, Loadstone, type LoadstoneConfig } from '@highlight/ui'
import { unsecureRandom } from '@shared/utils/random'
import { SubagentChildItem } from '../tools/tool-call-list-item'
import { resolveToolView, type ToolCallViewData } from '../tools/tool-view'
import { useToolCallsMap } from '../tools/tool-calls-context'
import { renderToolCard } from '../tools/registry'
import { PartLeaf } from '../parts/registry'
import type { ResolvedPart } from '../hooks/use-interleaved-parts'
import type { ToolCall } from '../../../../types'
import {
  askUserStagedActionSnapshot,
  isAwaitingAskUserResult,
  rendersInlineAskUserCard,
} from '../../../../integrations/ask-user-inline'
import { ANSWERABLE_PENDING_CONTENT } from '../../../../integrations/answerable'
import {
  MiniAskUserActionsResolver,
  type MiniAskUserActionsState,
  type MiniAskUserActionItem,
} from './mini-ask-user-actions'
import { ToolCallSummaryContent } from './tool-call-summary-content'

const EMPTY_ASK_USER_ACTIONS_STATE: MiniAskUserActionsState = { needsInput: false }
// A downward arrow on a 5×5 grid, lit top to bottom (`order` is the row).
const NEEDS_INPUT_ARROW_CELLS: ReadonlyArray<readonly [row: number, column: number]> = [
  [0, 2],
  [1, 2],
  [2, 0],
  [2, 2],
  [2, 4],
  [3, 1],
  [3, 2],
  [3, 3],
  [4, 2],
]
const NEEDS_INPUT_LOADSTONE_CONFIG = {
  rows: 5,
  columns: 5,
  animation: 'pop',
  duration: 850,
  stagger: 70,
  gap: 3,
  radius: 3,
  ledMode: false,
  cells: NEEDS_INPUT_ARROW_CELLS.map(([row, column]) => ({
    row,
    column,
    order: row,
    lightColor: '#000000',
    darkColor: 'var(--brand)',
    opacity: 0.75,
    scale: 0.6,
  })),
} satisfies LoadstoneConfig

function NeedsInputSummaryContent({ prompt }: { prompt?: string }) {
  return (
    <ToolCallSummaryContent
      indicator={
        <span aria-hidden="true" className="size-3.5 shrink-0">
          <Loadstone config={NEEDS_INPUT_LOADSTONE_CONFIG} size={14} />
        </span>
      }
    >
      {prompt ?? ANSWERABLE_PENDING_CONTENT}
    </ToolCallSummaryContent>
  )
}

const THINKING_WORDS = [
  'Thinking',
  'Reasoning',
  'Pondering',
  'Musing',
  'Cogitating',
  'Imagining',
  'Contemplating',
  'Deliberating',
  'Reflecting',
  'Brainstorming',
  'Mulling',
  'Frolicking',
  'Wondering',
  'Percolating',
  'Processing',
  'Conjuring',
  'Hatching',
  'Brewing',
  'Stewing',
  'Puzzling',
  'Inferring',
  'Deducing',
  'Daydreaming',
  'Noodling',
  'Meditating',
  'Marinating',
  'Speculating',
  'Envisioning',
  'Synthesizing',
  'Unraveling',
  'Parsing',
  'Cooking',
  'Churning',
  'Plotting',
  'Scheming',
  'Ruminating',
  'Computing',
  'Strategizing',
  'Pontificating',
  'Wandering',
]

const randomThinkingWord = () => THINKING_WORDS[Math.floor(unsecureRandom() * THINKING_WORDS.length)]

function buildSummaryText(
  toolParts: Array<ResolvedPart>,
  textCount: number,
  reasoningCount: number,
  allToolCallsMap: Map<string, ToolCall>,
): string {
  let toolCount = 0
  let agentCount = 0
  for (const part of toolParts) {
    if (part.type === 'tool_call') {
      if (part.toolCall.toolName === 'subagent') {
        const live = allToolCallsMap.get(part.toolCall.id)
        const subagents = live?.subagents ?? part.toolCall.subagents ?? []
        agentCount += subagents.length > 0 ? subagents.length : 1
      } else {
        toolCount++
      }
    }
  }

  const parts: Array<string> = []
  if (toolCount > 0) parts.push(`${toolCount} tool${toolCount !== 1 ? 's' : ''}`)
  if (agentCount > 0) parts.push(`${agentCount} agent${agentCount !== 1 ? 's' : ''}`)
  if (reasoningCount > 0) parts.push(`${reasoningCount} reasoning`)
  if (textCount > 0) parts.push(`${textCount} message${textCount !== 1 ? 's' : ''}`)
  return parts.join(' · ')
}

/** The three things the collapsible trigger can be. */
type TriggerContent = { kind: 'summary' } | { kind: 'thinking' } | { kind: 'tool'; view: ToolCallViewData }

interface ToolCallGroupProps {
  listParts: Array<ResolvedPart>
  needsInput: boolean
  needsInputPrompt?: string
  isStreaming: boolean
  justRevealedRef: RefObject<Set<string>>
  expandedToolIds: Set<string>
  onToolOpenChange: (toolId: string, isOpen: boolean) => void
  expandedSubagentKeys: Set<string>
  onSubagentOpenChange: (toolCallId: string, index: number, isOpen: boolean) => void
  messageId?: string
  conversationId?: string
}

const ToolCallGroup = memo(function ToolCallGroup({
  listParts,
  needsInput,
  needsInputPrompt,
  isStreaming,
  justRevealedRef,
  expandedToolIds,
  onToolOpenChange,
  expandedSubagentKeys,
  onSubagentOpenChange,
  messageId,
  conversationId,
}: ToolCallGroupProps) {
  const [open, setOpen] = useState(false)
  const [thinkingLabel, setThinkingLabel] = useState(randomThinkingWord)
  const allToolCallsMap = useToolCallsMap()

  const lastPart = listParts[listParts.length - 1]
  const isLastEntryThinking = lastPart?.type === 'thinking'
  const isLastEntryText = lastPart?.type === 'text'

  const toolParts = useMemo(() => listParts.filter((part) => part.type === 'tool_call'), [listParts])

  const lastToolPart = toolParts[toolParts.length - 1]
  const lastToolCall = lastToolPart?.type === 'tool_call' ? lastToolPart.toolCall : null
  const lastToolView = lastToolCall ? resolveToolView(lastToolCall) : null

  const textCount = useMemo(() => listParts.filter((part) => part.type === 'text').length, [listParts])
  const reasoningCount = useMemo(() => listParts.filter((part) => part.type === 'thinking').length, [listParts])
  const summaryText = useMemo(
    () => buildSummaryText(toolParts, textCount, reasoningCount, allToolCallsMap),
    [toolParts, textCount, reasoningCount, allToolCallsMap],
  )

  // What the trigger shows, decided once: a settled turn summarises, a live
  // turn shows the running tool, and anything else is the thinking label. The
  // label state below and the JSX both read this, so they can't disagree.
  const trigger: TriggerContent =
    needsInput || !isStreaming
      ? { kind: 'summary' }
      : open || isLastEntryThinking || isLastEntryText || !lastToolView
        ? { kind: 'thinking' }
        : { kind: 'tool', view: lastToolView }

  // Re-pick a random word every time the thinking label reappears (e.g. after
  // a tool icon finishes and the trigger switches back to the label).
  const prevTriggerKindRef = useRef(trigger.kind)
  useEffect(() => {
    if (trigger.kind === 'thinking' && prevTriggerKindRef.current !== 'thinking') {
      setThinkingLabel(randomThinkingWord())
    }
    prevTriggerKindRef.current = trigger.kind
  }, [trigger.kind])

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger
        className="relative flex w-full min-w-0 items-center justify-start gap-1 p-0 pb-1 text-[12px] font-normal"
        showIcon={false}
      >
        <span className="pointer-events-none flex min-w-0 items-center gap-1.5">
          {trigger.kind === 'tool' ? (
            <>
              {trigger.view.iconUrl ? (
                <img
                  src={trigger.view.iconUrl}
                  alt=""
                  className="size-3.5 shrink-0 rounded object-contain dark:invert"
                />
              ) : (
                <trigger.view.icon size={14} className="text-foreground-secondary shrink-0" strokeWidth={2.25} />
              )}
              <span className="text-foreground-secondary line-clamp-1 text-[12px] leading-4">{trigger.view.name}</span>
            </>
          ) : needsInput ? (
            <NeedsInputSummaryContent prompt={needsInputPrompt} />
          ) : (
            <ToolCallSummaryContent>{trigger.kind === 'thinking' ? thinkingLabel : summaryText}</ToolCallSummaryContent>
          )}
        </span>
        <ChevronRight
          size={13}
          className="text-foreground-placeholder shrink-0 transition-transform duration-200 group-data-[panel-open]:rotate-90"
        />
      </Collapsible.Trigger>

      <Collapsible.Content innerClassName="mt-0 gap-0">
        <div className="flex flex-col pb-3">
          {listParts.map((part) => {
            const subagents = (() => {
              if (part.type !== 'tool_call' || part.toolCall.toolName !== 'subagent') return []
              const live = allToolCallsMap.get(part.toolCall.id)
              return live?.subagents ?? part.toolCall.subagents ?? []
            })()
            return (
              <Fragment key={part.stableKey}>
                <div>
                  <PartLeaf
                    part={part}
                    justRevealedRef={justRevealedRef}
                    expandedToolIds={expandedToolIds}
                    onOpenChange={onToolOpenChange}
                    messageId={messageId}
                    conversationId={conversationId}
                  />
                </div>
                {subagents.map((subagent) => {
                  const toolCallId = part.type === 'tool_call' ? part.toolCall.id : ''
                  const key = `${toolCallId}:${subagent.index}`
                  return (
                    <SubagentChildItem
                      key={subagent.index}
                      subagent={subagent}
                      isOpen={expandedSubagentKeys.has(key)}
                      onOpenChange={(idx, isOpen) => onSubagentOpenChange(toolCallId, idx, isOpen)}
                    />
                  )
                })}
              </Fragment>
            )
          })}
        </div>
      </Collapsible.Content>
    </Collapsible>
  )
})

interface ToolCallListProps {
  parts: Array<ResolvedPart>
  isStreaming: boolean
  justRevealedRef: RefObject<Set<string>>
  messageId?: string
  conversationId?: string
}

export const ToolCallList = memo(function ToolCallList({
  parts,
  isStreaming,
  justRevealedRef,
  messageId,
  conversationId,
}: ToolCallListProps) {
  const [expandedToolIds, setExpandedToolIds] = useState<Set<string>>(() => new Set())
  const [expandedSubagentKeys, setExpandedSubagentKeys] = useState<Set<string>>(() => new Set())
  const allToolCallsMap = useToolCallsMap()

  const handleSubagentOpenChange = useCallback((toolCallId: string, index: number, isOpen: boolean) => {
    const key = `${toolCallId}:${index}`
    setExpandedSubagentKeys((prev) => {
      const next = new Set(prev)
      if (isOpen) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  const handleToolOpenChange = useCallback((toolId: string, isOpen: boolean) => {
    setExpandedToolIds((prev) => {
      const next = new Set(prev)
      if (isOpen) next.add(toolId)
      else next.delete(toolId)
      return next
    })
  }, [])

  if (parts.length === 0) {
    return null
  }

  // Surfaced ask-user calls are drawn by the floating question bar, so they only
  // contribute their waiting state here. A call still executing joins them
  // rather than the group: it is a question on its way to being surfaced, and
  // one continuous waiting state is what keeps the card from flashing into the
  // thread and back out when the marker lands. The legacy inline flavor still
  // has to come out of the group — a collapsed "N tools" trigger would hide the
  // question the turn is stuck on.
  const askUserActions: Array<MiniAskUserActionItem> = []
  const inlineAskUserParts: Array<ResolvedPart> = []
  // A tool with a registry card (an offered download, the files the agent
  // viewed) leaves the group too, so the user sees it without opening it.
  const toolCards: Array<{ key: string; card: ReactNode }> = []
  const groupedParts: Array<ResolvedPart> = []
  let awaitingAskUserResult = false
  for (const part of parts) {
    if (part.type !== 'tool_call') {
      groupedParts.push(part)
      continue
    }
    const card = renderToolCard(allToolCallsMap.get(part.toolCall.id) ?? part.toolCall, conversationId)
    if (card) {
      toolCards.push({ key: part.stableKey, card })
      continue
    }
    const action = askUserStagedActionSnapshot(part.toolCall)
    if (action) {
      askUserActions.push({ action, toolCall: part.toolCall })
    } else if (isAwaitingAskUserResult(part.toolCall)) {
      awaitingAskUserResult = true
    } else if (rendersInlineAskUserCard(part.toolCall)) {
      inlineAskUserParts.push(part)
    } else {
      groupedParts.push(part)
    }
  }
  const renderContent = (askUserState: MiniAskUserActionsState) => {
    const needsInput = askUserState.needsInput || awaitingAskUserResult
    return (
      <div className="flex flex-col gap-1.5">
        {groupedParts.length > 0 && (
          <ToolCallGroup
            listParts={groupedParts}
            needsInput={needsInput}
            needsInputPrompt={askUserState.prompt}
            isStreaming={isStreaming}
            justRevealedRef={justRevealedRef}
            expandedToolIds={expandedToolIds}
            onToolOpenChange={handleToolOpenChange}
            expandedSubagentKeys={expandedSubagentKeys}
            onSubagentOpenChange={handleSubagentOpenChange}
            messageId={messageId}
            conversationId={conversationId}
          />
        )}
        {groupedParts.length === 0 && inlineAskUserParts.length === 0 && needsInput && (
          <div className="flex min-w-0 items-center justify-start pb-1">
            <NeedsInputSummaryContent prompt={askUserState.prompt} />
          </div>
        )}
        {inlineAskUserParts.map((part) => (
          <PartLeaf
            key={part.stableKey}
            part={part}
            justRevealedRef={justRevealedRef}
            expandedToolIds={expandedToolIds}
            onOpenChange={handleToolOpenChange}
            messageId={messageId}
            conversationId={conversationId}
          />
        ))}
        {toolCards.map(({ key, card }) => (
          <Fragment key={key}>{card}</Fragment>
        ))}
      </div>
    )
  }

  return askUserActions.length > 0 ? (
    <MiniAskUserActionsResolver items={askUserActions}>{renderContent}</MiniAskUserActionsResolver>
  ) : (
    renderContent(EMPTY_ASK_USER_ACTIONS_STATE)
  )
})
